import {
  ApplicationRef,
  effect,
  EventEmitter,
  inject,
  Injectable,
  signal
} from "@angular/core";
import PQueue from "p-queue";
import { lastValueFrom } from "rxjs";
import { StorageBackend } from "src/app/backend/api/storage/storage.backend";
import { BeforeUnloadService } from "src/app/services/before-unload.service";
import { FileSystemStorageService } from "src/app/services/file-system-storage.service";
import { MemberStorageService } from "src/app/services/member-storage.service";
import { UploadProgressInfo } from "src/app/support/upload-progress";
import { uuid } from "src/app/support/uuid";

import { sntLog } from "src/app/common/logger";

const logger = sntLog("UploadManagerService");
const UPLOAD_MANAGER_STORAGE_KEY = "uploadManager";

export type UploadManagerStorageInfo = {
  name: string;
  size: number;
  localStorageId?: string;
  remoteStorageId?: string;
};

export type UploadManagerUpload = {
  id: string;
  status: UplaodManagerUploadStatus;
  file: UploadManagerStorageInfo;
  importStrategy: string | undefined;
  sharings?: Array<Record<string, string>>;
};

export enum UplaodManagerUploadStatus {
  PENDING = "pending",
  QUEUED = "queued",
  UPLOADING = "uploading",
  DONE = "uploaded",
  ERROR = "error",
  IMPORTED = "imported",
  IMPORT_FAILED = "import-failed"
}

export enum UplaodManagerStatus {
  STANDBY = "standby",
  UPLOADING = "uploading"
}

export type UploadManagerImportStrategy = (
  upload: UploadManagerUpload
) => Promise<void>;

@Injectable()
export class UploadManagerService {
  readonly #uploadQueue = new PQueue({ concurrency: 1 });
  readonly #pendingTasks = new Map<string, AbortController>();

  readonly #fileSystemStorageService = inject(FileSystemStorageService);
  readonly #memberStorageService = inject(MemberStorageService);
  readonly #applicationRef = inject(ApplicationRef);
  readonly #storageBackend = inject(StorageBackend);
  readonly #beforeUnloadService = inject(BeforeUnloadService);

  readonly uploadProgress = new EventEmitter<{
    uploadId: string;
    uploadProgress: UploadProgressInfo;
  }>();

  readonly uploadChange = new EventEmitter<UploadManagerUpload>(true);

  readonly #importStrategies: Map<string, UploadManagerImportStrategy> =
    new Map();

  #uploads = signal<UploadManagerUpload[]>(
    this.#memberStorageService
      .getItemOrDefault<UploadManagerUpload[]>(UPLOAD_MANAGER_STORAGE_KEY, [])
      .map((item) => {
        if (
          item.status !== UplaodManagerUploadStatus.DONE &&
          item.status !== UplaodManagerUploadStatus.IMPORTED &&
          item.status !== UplaodManagerUploadStatus.IMPORT_FAILED
        ) {
          item.status = UplaodManagerUploadStatus.PENDING;
        }

        return item;
      })
  );

  status = signal<UplaodManagerStatus>(UplaodManagerStatus.STANDBY);

  constructor() {
    this.#regisaterQueueListeners();
    this.#registerEffects();
  }

  #regisaterQueueListeners(): void {
    this.#uploadQueue.on("active", () => {
      logger.debug("queue active");
      this.status.set(UplaodManagerStatus.UPLOADING);
      this.#beforeUnloadService.acquire();
    });

    this.#uploadQueue.on("idle", () => {
      logger.debug("queue idle");
      this.status.set(UplaodManagerStatus.STANDBY);
      this.#beforeUnloadService.release();
    });
  }

  #registerEffects(): void {
    effect(() => {
      this.#onUploadsEffect(this.#uploads());
    });
  }

  #onUploadsEffect(uploads: UploadManagerUpload[]): void {
    const uploadsToPersist = uploads.filter(
      (upload: UploadManagerUpload) =>
        upload.status !== UplaodManagerUploadStatus.IMPORTED
    );

    this.#memberStorageService.setItem(
      UPLOAD_MANAGER_STORAGE_KEY,
      JSON.stringify(uploadsToPersist)
    );

    this.status.set(
      uploads.some(
        (upload: UploadManagerUpload) =>
          upload.status === UplaodManagerUploadStatus.UPLOADING
      )
        ? UplaodManagerStatus.UPLOADING
        : UplaodManagerStatus.STANDBY
    );

    const pendingUploads = uploads.filter(
      (item) => item.status === UplaodManagerUploadStatus.PENDING
    );

    pendingUploads.forEach((pendingUpload: UploadManagerUpload) => {
      pendingUpload.status = UplaodManagerUploadStatus.QUEUED;
      this.#uploads.update((uploads: UploadManagerUpload[]) => {
        return [...uploads];
      });
      this.uploadChange.emit(pendingUpload);

      const abortController = new AbortController();
      this.#pendingTasks.set(pendingUpload.id, abortController);

      this.#uploadQueue.add(
        this.#doQueueJobFactory({ abortController, pendingUpload })
      );
    });
  }

  #doQueueJobFactory({
    abortController,
    pendingUpload
  }: {
    abortController: AbortController;
    pendingUpload: UploadManagerUpload;
  }): () => Promise<void> {
    return async () => {
      if (abortController.signal.aborted) {
        this.#pendingTasks.delete(pendingUpload.id);
        return;
      }

      await this.#applicationRef.whenStable();

      const uploadSuccessful: boolean = await this.#upload({
        upload: pendingUpload,
        abortController,
        onProgress: (progressInfo: UploadProgressInfo) => {
          this.uploadProgress.emit({
            uploadId: pendingUpload.id,
            uploadProgress: progressInfo
          });
        }
      });

      this.#pendingTasks.delete(pendingUpload.id);

      if (uploadSuccessful && pendingUpload.importStrategy) {
        const importStrategyFn: UploadManagerImportStrategy | undefined =
          this.#importStrategies.get(pendingUpload.importStrategy);

        if (importStrategyFn) {
          try {
            await importStrategyFn(pendingUpload);

            pendingUpload.status = UplaodManagerUploadStatus.IMPORTED;
            this.#uploads.update((uploads: UploadManagerUpload[]) => {
              return [...uploads];
            });
            this.uploadChange.emit(pendingUpload);
          } catch (ex) {
            pendingUpload.status = UplaodManagerUploadStatus.IMPORT_FAILED;
            this.#uploads.update((uploads: UploadManagerUpload[]) => {
              return [...uploads];
            });
            this.uploadChange.emit(pendingUpload);
          }
        }
      }
    };
  }

  registerImportStrategy({
    name,
    strategy
  }: {
    name: string;
    strategy: UploadManagerImportStrategy;
  }): void {
    this.#importStrategies.set(name, strategy);
  }

  async add({
    file,
    importStrategy,
    sharings
  }: {
    file: File;
    importStrategy: string | undefined;
    sharings?: Array<Record<string, string>>;
  }): Promise<string> {
    const storageId = await this.#fileSystemStorageService.create({
      content: file
    });

    const upload: UploadManagerUpload = {
      id: uuid(),
      importStrategy,
      file: {
        name: file.name,
        size: file.size,
        localStorageId: storageId,
        remoteStorageId: undefined
      },
      status: UplaodManagerUploadStatus.PENDING,
      sharings
    };

    this.#uploads.update((uploads: UploadManagerUpload[]) => {
      return [upload, ...uploads];
    });

    return upload.id;
  }

  async remove({ id }: { id: string }): Promise<void> {
    const upload: UploadManagerUpload | undefined = this.#uploads().find(
      (upload) => upload.id === id
    );

    if (!upload) {
      return;
    }

    // Abort pending task if exists
    const abortController = this.#pendingTasks.get(id);
    if (abortController) {
      abortController.abort();
      this.#pendingTasks.delete(id);
    }

    // Remove from uploads list
    this.#uploads.update((uploads: UploadManagerUpload[]) => {
      return uploads.filter((item) => item.id !== id);
    });

    if (upload.file.localStorageId) {
      await this.#fileSystemStorageService.remove(upload.file.localStorageId);
    }

    if (upload.file.remoteStorageId) {
      lastValueFrom(this.#storageBackend.delete(upload.file.remoteStorageId));
    }
  }

  getStorageInfo({ id }: { id: string }): UploadManagerStorageInfo | undefined {
    const upload = this.#uploads().find((upload) => upload.id === id);

    if (!upload) {
      return undefined;
    }

    return upload.file;
  }

  getStatus({ id }: { id: string }): UplaodManagerUploadStatus | undefined {
    return this.#uploads().find((upload) => upload.id === id)?.status;
  }

  async #upload({
    upload,
    onProgress,
    abortController
  }: {
    upload: UploadManagerUpload;
    abortController: AbortController;
    onProgress?: (progressInfo: UploadProgressInfo) => void;
  }): Promise<boolean> {
    upload.status = UplaodManagerUploadStatus.UPLOADING;
    this.#uploads.update((uploads: UploadManagerUpload[]) => {
      return [...uploads];
    });
    this.uploadChange.emit(upload);

    try {
      const uploadResult = await this.#storageBackend.uploadV2({
        name: upload.file.name,
        body: await this.#fileSystemStorageService.load({
          name: upload.file.localStorageId!
        }),
        abortController,
        storageType: "temporary",
        sharings: upload.sharings,
        onProgress
      });

      await this.#fileSystemStorageService.remove(upload.file.localStorageId!);

      this.#uploads.update((uploads: UploadManagerUpload[]) => {
        return uploads.map((item: UploadManagerUpload) => {
          if (item.id === upload.id) {
            item.status = UplaodManagerUploadStatus.DONE;
            item.file.localStorageId = undefined;
            item.file.remoteStorageId = uploadResult.storageId;

            this.uploadChange.emit(item);
          }

          return item;
        });
      });

      return true;
    } catch (exception) {
      logger.error(exception);

      upload.status = UplaodManagerUploadStatus.ERROR;
      this.#uploads.update((uploads: UploadManagerUpload[]) => {
        return [...uploads];
      });

      this.uploadChange.emit(upload);

      return false;
    }
  }
}
